import { useState, useEffect } from 'react';
import { Package, Plus, AlertTriangle, CheckCircle2, QrCode, Scan, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ordenesAPI } from '@/lib/api';
import { toast } from 'sonner';
import MaterialPickerModal from '@/components/MaterialPickerModal';
import MaterialEstadoBadge from '@/components/MaterialEstadoBadge';

export function TecnicoMaterialesCard({ orden, repuestos, onRefresh, onMaterialesChange }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [guardandoMaterial, setGuardandoMaterial] = useState(false);
  
  // Estado local de materiales para evitar recargar toda la página
  const [localMateriales, setLocalMateriales] = useState(orden.materiales || []);
  
  // Sincronizar cuando cambia la orden desde el padre
  useEffect(() => {
    setLocalMateriales(orden.materiales || []);
  }, [orden.materiales]);
  
  // Estados para validación
  const [showValidacionModal, setShowValidacionModal] = useState(false);
  const [codigoValidacion, setCodigoValidacion] = useState('');
  const [validando, setValidando] = useState(false);

  // Helper para notificar cambios al padre
  const notifyMaterialesChange = (nuevosMateriales) => {
    if (onMaterialesChange) {
      onMaterialesChange(nuevosMateriales);
    }
  };

  const handleMaterialAdded = (data) => {
    // Callback del MaterialPickerModal tras un add exitoso (técnico añade material).
    const nuevoMaterial = data?.material;
    if (nuevoMaterial) {
      const nuevosMateriales = [...localMateriales, nuevoMaterial];
      setLocalMateriales(nuevosMateriales);
      notifyMaterialesChange(nuevosMateriales);
    }
    toast.warning('Material añadido. La orden ha sido BLOQUEADA hasta que el administrador la apruebe.');
  };

  // Validar material por código (SKU o código de barras)
  const handleValidarPorCodigo = async () => {
    if (!codigoValidacion.trim()) {
      toast.error('Introduce o escanea el código del material');
      return;
    }
    
    setValidando(true);
    try {
      const res = await ordenesAPI.validarMaterialPorCodigo(orden.id, codigoValidacion.trim());
      
      if (res.data.ya_validado) {
        toast.info(`"${res.data.material.nombre}" ya estaba validado`);
      } else {
        toast.success(`✓ ${res.data.material.nombre} validado (${res.data.total - res.data.pendientes}/${res.data.total})`);
        
        // Actualizar estado local - marcar el material como validado
        const updatedMateriales = localMateriales.map((m, i) => 
          i === res.data.index ? { ...m, validado_tecnico: true } : m
        );
        setLocalMateriales(updatedMateriales);
        notifyMaterialesChange(updatedMateriales);
      }
      
      setCodigoValidacion('');
      
      // Si ya no quedan materiales pendientes, cerrar modal
      if (res.data.pendientes === 0) {
        toast.success('🎉 ¡Todos los materiales han sido validados!');
        setShowValidacionModal(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Material no encontrado en esta orden');
    } finally {
      setValidando(false);
    }
  };

  // Validar material individual (click en botón)
  const handleValidarMaterial = async (index) => {
    try {
      await ordenesAPI.validarMaterial(orden.id, index);
      toast.success('Material validado');
      
      // Actualizar estado local
      const updatedMateriales = localMateriales.map((m, i) => 
        i === index ? { ...m, validado_tecnico: true } : m
      );
      setLocalMateriales(updatedMateriales);
      notifyMaterialesChange(updatedMateriales);
    } catch (error) {
      toast.error('Error al validar material');
    }
  };

  // Calcular estadísticas de validación
  const materialesValidados = localMateriales.filter(m => m.validado_tecnico).length;
  const materialesPendientes = localMateriales.length - materialesValidados;
  const porcentajeValidado = localMateriales.length > 0 ? (materialesValidados / localMateriales.length) * 100 : 0;
  const todosValidados = localMateriales.length > 0 && materialesPendientes === 0;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Materiales
            {localMateriales.length > 0 && (
              <Badge variant={todosValidados ? "default" : "secondary"} className="ml-2">
                {materialesValidados}/{localMateriales.length} validados
              </Badge>
            )}
          </CardTitle>
          <div className="flex gap-2">
            {localMateriales.length > 0 && (
              <Button 
                variant={materialesPendientes > 0 ? "default" : "outline"}
                size="sm"
                onClick={() => setShowValidacionModal(true)}
                data-testid="validar-materiales-btn"
              >
                <Scan className="w-4 h-4 mr-1" />
                {materialesPendientes > 0 ? `Validar (${materialesPendientes})` : 'Revisar'}
              </Button>
            )}
            {!orden.bloqueada && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPickerOpen(true)}
                data-testid="add-material-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Añadir material
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Barra de progreso de validación */}
          {localMateriales.length > 0 && (
            <div className="mb-4">
              <div className="flex justify-between text-sm text-muted-foreground mb-1">
                <span>Progreso de validación</span>
                <span>{Math.round(porcentajeValidado)}%</span>
              </div>
              <Progress value={porcentajeValidado} className="h-2" />
            </div>
          )}
          
          {localMateriales.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No hay materiales asignados</p>
              <p className="text-sm">Añade los materiales necesarios para la reparación</p>
            </div>
          ) : (
            <div className="space-y-2">
              {localMateriales.map((material, index) => (
                <div 
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    material.validado_tecnico 
                      ? 'border-green-200 bg-green-50' 
                      : !material.aprobado 
                        ? 'border-orange-300 bg-orange-50' 
                        : 'bg-muted/50 border-border'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {material.validado_tecnico ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : !material.aprobado ? (
                      <AlertTriangle className="w-5 h-5 text-orange-500" />
                    ) : (
                      <Package className="w-5 h-5 text-muted-foreground/70" />
                    )}
                    <div>
                      <p className="font-medium">{material.nombre}</p>
                      <p className="text-xs text-muted-foreground">
                        Cantidad: {material.cantidad}
                        {material.sku && <span className="ml-2">SKU: {material.sku}</span>}
                        {material.validado_tecnico && (
                          <span className="text-green-600 ml-2">
                            ✓ Validado {material.validado_at?.slice(0, 10)}
                          </span>
                        )}
                        {material.añadido_por_tecnico && !material.aprobado && (
                          <span className="text-orange-600 ml-2">(Pendiente aprobación)</span>
                        )}
                      </p>
                      <div className="mt-1">
                        <MaterialEstadoBadge material={material} compact />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!material.validado_tecnico && material.aprobado && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleValidarMaterial(index)}
                        className="text-green-600 hover:text-green-700"
                      >
                        <Check className="w-3 h-3 mr-1" />
                        Validar
                      </Button>
                    )}
                    <Badge variant={
                      material.validado_tecnico ? "default" : 
                      material.aprobado ? "secondary" : "outline"
                    }>
                      {material.validado_tecnico ? 'Validado' : 
                       material.aprobado ? 'Pendiente' : 'No aprobado'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Validación con Escáner */}
      <Dialog open={showValidacionModal} onOpenChange={setShowValidacionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scan className="w-5 h-5" />
              Validar Materiales
            </DialogTitle>
            <DialogDescription>
              Escanea o introduce el SKU/código de barras de cada material para confirmar que ha sido utilizado.
            </DialogDescription>
          </DialogHeader>
          
          {/* Progreso */}
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="font-medium">
                {todosValidados ? '¡Todos validados!' : `${materialesPendientes} pendientes`}
              </span>
              <span>{materialesValidados}/{localMateriales.length}</span>
            </div>
            <Progress value={porcentajeValidado} className="h-3" />
          </div>
          
          {/* Input de escaneo */}
          {!todosValidados && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <QrCode className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Escanea o escribe el código..."
                    className="pl-10 font-mono"
                    value={codigoValidacion}
                    onChange={(e) => setCodigoValidacion(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleValidarPorCodigo()}
                    autoFocus
                    data-testid="codigo-validacion-input"
                  />
                </div>
                <Button onClick={handleValidarPorCodigo} disabled={validando}>
                  {validando ? 'Validando...' : 'Validar'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Presiona Enter después de escanear para validar automáticamente
              </p>
            </div>
          )}
          
          {/* Lista de materiales */}
          <div className="space-y-2 max-h-64 overflow-y-auto mt-4">
            <p className="text-sm font-medium text-muted-foreground">Materiales asignados:</p>
            {localMateriales.map((material, index) => (
              <div 
                key={index}
                className={`flex items-center justify-between p-2 rounded border text-sm ${
                  material.validado_tecnico ? 'bg-green-50 border-green-200' : 'bg-muted/50'
                }`}
              >
                <div className="flex items-center gap-2">
                  {material.validado_tecnico ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  ) : (
                    <Package className="w-4 h-4 text-muted-foreground/70" />
                  )}
                  <span>{material.nombre}</span>
                </div>
                {material.validado_tecnico ? (
                  <Badge variant="default" className="text-xs">✓</Badge>
                ) : (
                  <Badge variant="outline" className="text-xs">Pendiente</Badge>
                )}
              </div>
            ))}
          </div>
          
          {todosValidados && (
            <div className="text-center py-4">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
              <p className="font-medium text-green-700">¡Todos los materiales validados!</p>
              <Button className="mt-3" onClick={() => setShowValidacionModal(false)}>
                Cerrar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal disciplinado de añadir material (técnico: solo búsqueda en inventario) */}
      <MaterialPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        ordenId={orden.id}
        addedByTechnician={true}
        disableCreateTab={true}
        onAdded={handleMaterialAdded}
      />
    </>
  );
}
